// NEW_FILE_JUSTIFIED: New component — IColumnSettingsPopoverProps in @types/tableColumnVisibility.ts defines its props interface; the component itself does not exist yet.
import { useMemo } from 'react';
import { SlidersHorizontal, RotateCcw } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { IColumnSettingsPopoverProps, IColumnConfig } from '@/@types/tableColumnVisibility';

/**
 * Popover with grouped checkboxes for configuring which table columns are visible.
 * Pinned columns are shown as disabled (always visible). Includes a "Reset" button.
 */
export function ColumnSettingsPopover({
  allColumns,
  isColumnVisible,
  toggleColumn,
  resetToDefaults,
}: IColumnSettingsPopoverProps) {
  // Group columns by their group field, preserving column order
  const groups = useMemo(() => {
    const groupMap = new Map<string, IColumnConfig[]>();
    for (const col of allColumns) {
      const existing = groupMap.get(col.group);
      if (existing) {
        existing.push(col);
      } else {
        groupMap.set(col.group, [col]);
      }
    }
    return Array.from(groupMap.entries());
  }, [allColumns]);

  // Count visible non-pinned columns
  const visibleCount = allColumns.filter(c => !c.pinned && isColumnVisible(c.id)).length;
  const toggleableCount = allColumns.filter(c => !c.pinned).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground hover:text-foreground"
          aria-label="Configure visible columns"
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-sm font-medium text-foreground">Columns</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={resetToDefaults}
          >
            <RotateCcw className="mr-1 h-3 w-3" />
            Reset
          </Button>
        </div>
        <Separator />

        {/* Column Groups */}
        <div className="max-h-[300px] overflow-y-auto py-1">
          {groups.map(([groupName, columns], groupIndex) => (
            <div key={groupName}>
              {groupIndex > 0 && <Separator className="my-1" />}
              <div className="px-3 py-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {groupName}
                </span>
              </div>
              {columns.map(col => (
                <label
                  key={col.id}
                  className={cn(
                    'flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-muted/50',
                    col.pinned && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <Checkbox
                    checked={isColumnVisible(col.id)}
                    onCheckedChange={() => toggleColumn(col.id)}
                    disabled={!!col.pinned}
                    aria-label={`Toggle ${col.label} column`}
                  />
                  <span
                    className={cn(
                      'text-sm',
                      col.pinned ? 'text-muted-foreground' : 'text-foreground'
                    )}
                  >
                    {col.label}
                  </span>
                  {col.pinned && (
                    <span className="ml-auto text-xs text-muted-foreground">Pinned</span>
                  )}
                </label>
              ))}
            </div>
          ))}
        </div>

        {/* Footer */}
        <Separator />
        <div className="px-3 py-2">
          <span className="text-xs text-muted-foreground">
            {visibleCount} of {toggleableCount} shown
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
